import React, { useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import {
  I2cInput,
  I2cMenuItem,
  I2cSelect,
  I2cDatePicker,
} from "@webcomponents/i2cwebcomponents/dist/react";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import "../Prepaid.scss";
import timezone from "./timezones.json";
import { currencies } from "../ProgramInformation/FieldsListingLabels";
/**
 * Component for capturing and validating project details.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.setFormValidStates - Function to set form validation states.
 * @returns {React.Component} - The rendered component.
 */
const ProjectDetails = ({
  setFormValidStates,
  setAddFormData,
  addFormData,
  formDataJson,
  isFetching,
}) => {
  // Define validation schema using Zod
  const validationSchema = z
    .object({
      pd_client_name: z.string().min(1, { message: "Client Name is required" }),
      pd_region: z.string().min(1, { message: "Region is required" }),
      pd_team_avail: z
        .string()
        .min(1, { message: "Team availability is required" }),
      pd_pid: z.string().optional(),
      pd_ticket_num: z.string().optional(),
      pd_kick_off: z.string().optional(),
      pd_go_live: z.string().optional(),
      pd_sales_rep: z.string().optional(),
      pd_pmo: z.string().min(1, { message: "Project manager is required" }),
      pd_solution_architect: z
        .string()
        .min(1, { message: "Solution Architect is required" }),
      pd_po_resource: z.string().optional(),
      pd_quality_engineer: z.string().optional(),
      pd_account_manager: z.string().optional(),
      pd_sales_engineer: z.string().optional(),
    })
    .refine(
      (data) => {
        const kickOffDate = new Date(data.pd_kick_off);
        const goLiveDate = new Date(data.pd_go_live);
        if (isNaN(kickOffDate.getDate())) return true;
        return goLiveDate > kickOffDate;
      },
      {
        message: "Go-Live date should be greater than Kick off date",
        path: ["pd_go_live"],
      },
    );
  // .refine(
  //   (data) => {
  //     const kickOffDate = new Date(data.pd_kick_off);
  //     const goLiveDate = new Date(data.pd_go_live);
  //     return goLiveDate < kickOffDate;
  //   },
  //   {
  //     message: "Kick off date should be less than Go Live date",
  //     path: ["pd_kick_off"],
  //   },
  // );

  // Initialize React Hook Form with Zod resolver
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      pd_client_name: "",
      pd_region: "",
      pd_team_avail: "",
      pd_pid: "",
      pd_ticket_num: "",
      pd_kick_off: "",
      pd_go_live: "",
      pd_sales_rep: "",
      pd_pmo: "",
      pd_solution_architect: "",
      pd_po_resource: "",
      pd_quality_engineer: "",
      pd_account_manager: "",
      pd_sales_engineer: "",
    },
  });

  const fields = [
    "pd_client_name",
    "pd_region",
    "pd_team_avail",
    "pd_pid",
    "pd_ticket_num",
    "pd_kick_off",
    "pd_go_live",
    "pd_sales_rep",
    "pd_pmo",
    "pd_solution_architect",
    "pd_po_resource",
    "pd_quality_engineer",
    "pd_account_manager",
    "pd_sales_engineer",
  ];
  const allRegions = [
    "Africa",
    "Central America",
    "Middle East",
    "Pacific",
    "Asia",
    "Europe",
    "North America",
    "South America",
  ];
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[1]["1_0"][field] ?? "");
      });
      methods.trigger();
    }
  }, [isFetching]);

  const {
    control,
    formState: { errors },
  } = methods;

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);

  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      1: {
        ...addFormData["1"],
        "1_0": { ...values },
      },
    });
  };
  // Updsate form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("1_0", true);
    else setFormValidStates("1_0", false);
  }, [Object.keys(errors).length]);

  const goLiveDate = new Date(methods.watch("pd_kick_off"));
  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-column-sm gap-row-md" className="section-space">
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_client_name"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    required
                    placeholder="Client Name"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Client Name"
                    name={"pd_client_name"}
                    size="large"
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.pd_client_name &&
                  methods.formState.touchedFields.pd_client_name
                    ? true
                    : false
                }
                message={errors.pd_client_name?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_region"
                control={control}
                render={({ field }) => (
                  <I2cSelect
                    {...field}
                    name="pd_region"
                    onI2cChange={(e) => {
                      methods.setValue("pd_region", e.target.value);
                      handleValueChanges();
                    }}
                    value={methods.watch("pd_region")}
                    label="Region"
                    required
                    placeholder="Region"
                    size="large"
                    search
                  >
                    {allRegions.map((regions, index) => (
                      <I2cMenuItem key={index + regions} value={regions}>
                        {regions}
                      </I2cMenuItem>
                    ))}
                  </I2cSelect>
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_region && methods.formState.touchedFields.pd_region
                    ? true
                    : false
                }
                message={errors.pd_region?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_team_avail"
                control={control}
                render={({ field }) => (
                  <I2cSelect
                    {...field}
                    label="Project Team Availability"
                    onI2cChange={(e) => {
                      methods.setValue("pd_team_avail", e.target.value);
                      handleValueChanges();
                    }}
                    value={methods.watch("pd_team_avail")}
                    placeholder="Time zone"
                    name="pd_team_avail"
                    required
                    size="large"
                    search
                  >
                    {timezone.map((timezoneList, index) => (
                      <I2cMenuItem key={index} value={timezoneList.value}>
                        {timezoneList.value}
                      </I2cMenuItem>
                    ))}
                  </I2cSelect>
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_team_avail &&
                  methods.formState.touchedFields.pd_team_avail
                    ? true
                    : false
                }
                message={errors.pd_team_avail?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_pid"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    label="Project ID"
                    onI2cInput={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                    value={methods.watch("pd_pid")}
                    placeholder="Project ID"
                    name="pd_pid"
                    size="large"
                  ></I2cInput>
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_pid && methods.formState.touchedFields.pd_pid
                    ? true
                    : false
                }
                message={errors.pd_pid?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_ticket_num"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    label="Ticket Number"
                    onI2cInput={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                   
                    value={methods.watch("pd_ticket_num")}
                    placeholder="Ticket Number"
                    name="pd_ticket_num"
                    size="large"
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_ticket_num &&
                  methods.formState.touchedFields.pd_ticket_num
                    ? true
                    : false
                }
                message={errors.pd_ticket_num?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_kick_off"
                control={control}
                render={({ field }) => (
                  <I2cDatePicker
                    {...field}
                    I2cDatePicker
                    dateFormat="d-m-Y"
                    onI2cDateApply={(e) => {
                      if (e.target?.appliedDate) {
                        const kickOffDate =
                          e.target?.appliedDate[0]?.toDateString() ?? "";
                        const goLiveDate = new Date(
                          methods.watch("pd_go_live"),
                        );
                        if (goLiveDate < new Date(kickOffDate)) {
                          methods.setError("pd_kick_off", {
                            message:
                              "Kick off date should be less than the go live date",
                          });
                        }
                        field.onChange(kickOffDate);
                      } else field.onChange("");
                      handleValueChanges();
                      methods.trigger();
                    }}
                    placeholder="Project kickoff date"
                    // minDate={new Date().setHours(0, 0, 0, 0)}
                    size="large"
                    name="pd_kick_off"
                    label="Project Kick-off Date"
                    defaultDate={
                      methods.watch("pd_kick_off")
                        ? new Date(methods.watch("pd_kick_off"))
                        : ""
                    }
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_kick_off &&
                  methods.formState.touchedFields.pd_kick_off
                    ? true
                    : false
                }
                message={errors.pd_kick_off?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_go_live"
                control={control}
                render={({ field }) => (
                  <I2cDatePicker
                    {...field}
                    name="pd_go_live"
                    I2cDatePicker
                    placeholder="Project Go-Live date"
                    minDate={
                      !isNaN(goLiveDate.getDate())
                        ? new Date(
                            goLiveDate.getFullYear(),
                            goLiveDate.getMonth(),
                            goLiveDate.getDate() + 1,
                            0,
                            0,
                            0,
                          )
                        : new Date(
                            new Date().getFullYear(),
                            new Date().getMonth(),
                            new Date().getDate() + 1,
                            0,
                            0,
                            0,
                          )
                    }
                    onI2cDateApply={(e) => {
                      if (e.target?.appliedDate)
                        field.onChange(
                          e.target?.appliedDate[0]?.toDateString() ?? "",
                        );
                      else field.onChange("");
                      handleValueChanges();
                    }}
                    value={methods.watch("pd_go_live")}
                    dateFormat="d-m-Y"
                    size="large"
                    label="Go-live Date (Projected)"
                    defaultDate={
                      methods.watch("pd_go_live")
                        ? new Date(methods.watch("pd_go_live"))
                        : ""
                    }
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_go_live &&
                  methods.formState.touchedFields.pd_go_live
                    ? true
                    : false
                }
                message={errors.pd_go_live?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="box-wrap multiple">
              <div className="box">
                <div className="single-field-wrapper">
                  <Controller
                    name="pd_sales_rep"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        label="i2c Inc. Sales Rep"
                        onI2cInput={(e) => {
                          field.onChange(e.target.value);
                          handleValueChanges();
                        }}
                        type="text"
                        value={methods.watch("pd_sales_rep")}
                        placeholder="i2c Inc. Sales Rep"
                        name="pd_sales_rep"
                        size="large"
                      />
                    )}
                  />
                  <ErrorMessage
                    show={
                      errors.pd_sales_rep &&
                      methods.formState.touchedFields.pd_sales_rep
                        ? true
                        : false
                    }
                    message={errors.pd_sales_rep?.message ?? "Invalid input"}
                  />
                </div>
              </div>
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="box-wrap multiple">
              <div className="box">
                <div className="single-field-wrapper">
                  <Controller
                    name="pd_pmo"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        label="i2c Inc. Project Manager(s)"
                        onI2cInput={(e) => {
                          field.onChange(e.target.value);
                          handleValueChanges();
                        }}
                        type="text"
                        value={methods.watch("pd_pmo")}
                        placeholder="i2c Inc. Project Manager(s)"
                        name="pd_pmo"
                        required
                        size="large"
                      />
                    )}
                  />
                  {/* <ErrorMessage
                    show={
                      errors.pd_pmo && methods.formState.touchedFields.pd_pmo
                        ? true
                        : false
                    }
                    message={errors.pd_pmo?.message ?? "Invalid input"}
                  /> */}
                </div>
              </div>
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="box-wrap multiple">
              <div className="box">
                <div className="single-field-wrapper">
                  <Controller
                    name="pd_solution_architect"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        label="i2c Inc. Solution Architect(s)"
                        onI2cInput={(e) => {
                          field.onChange(e.target.value);
                          handleValueChanges();
                        }}
                        type="text"
                        value={methods.watch("pd_solution_architect")}
                        placeholder="i2c Inc. Solution Architect(s)"
                        name="pd_solution_architect"
                        required
                        size="large"
                      />
                    )}
                  />
                  {/* <ErrorMessage
                    show={
                      errors.pd_solution_architect &&
                      methods.formState.touchedFields.pd_solution_architect
                        ? true
                        : false
                    }
                    message={
                      errors.pd_solution_architect?.message ?? "Invalid input"
                    }
                  /> */}
                </div>
              </div>
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_po_resource"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    label="PO Resource"
                    onI2cInput={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                    type="text"
                    value={methods.watch("pd_po_resource")}
                    placeholder="PO Resource"
                    name="pd_po_resource"
                    size="large"
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_po_resource &&
                  methods.formState.touchedFields.pd_po_resource
                    ? true
                    : false
                }
                message={errors.pd_po_resource?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_quality_engineer"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    label="Quality Engineer"
                    onI2cInput={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                    type="text"
                    value={methods.watch("pd_quality_engineer")}
                    placeholder="Quality Engineer"
                    name="pd_quality_engineer"
                    size="large"
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_quality_engineer &&
                  methods.formState.touchedFields.pd_quality_engineer
                    ? true
                    : false
                }
                message={errors.pd_quality_engineer?.message ?? "Invalid input"}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="sub-heading mb-16">i2c Contact Details</div>
          <div i2c-grid="grid gap-column-sm gap-row-md">
            <div i2c-grid="6@md">
              <div className="single-field-wrapper">
                <Controller
                  name="pd_account_manager"
                  control={control}
                  render={({ field }) => (
                    <I2cInput
                      {...field}
                      label="i2c Account Manager(s)"
                      onI2cInput={(e) => {
                        field.onChange(e.target.value);
                        handleValueChanges();
                      }}
                      type="text"
                      value={methods.watch("pd_account_manager")}
                      placeholder="i2c Account Manager(s)"
                      name="pd_account_manager"
                      size="large"
                    />
                  )}
                />
                <ErrorMessage
                  show={
                    errors.pd_account_manager &&
                    methods.formState.touchedFields.pd_account_manager
                      ? true
                      : false
                  }
                  message={
                    errors.pd_account_manager?.message ?? "Invalid input"
                  }
                />
              </div>
            </div>
            <div i2c-grid="6@md">
              <div className="single-field-wrapper">
                <Controller
                  name="pd_sales_engineer"
                  control={control}
                  render={({ field }) => (
                    <I2cInput
                      {...field}
                      label="i2c Sales Engineer"
                      onI2cInput={(e) => {
                        field.onChange(e.target.value);
                        handleValueChanges();
                      }}
                      type="text"
                      value={methods.watch("pd_sales_engineer")}
                      placeholder="i2c Sales Engineer"
                      name="pd_sales_engineer"
                      size="large"
                    />
                  )}
                />
                <ErrorMessage
                  show={
                    errors.pd_sales_engineer &&
                    methods.formState.touchedFields.pd_sales_engineer
                      ? true
                      : false
                  }
                  message={errors.pd_sales_engineer?.message ?? "Invalid input"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProjectDetails;
